import React, { useState } from 'react';
import { Heart, Coffee } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import MorphButton from './MorphButton';
import CurvedLoop from './CurvedLoop';
import './Footer.css';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((value || '').trim());

  const canSubmitEmail = isValidEmail(email);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!canSubmitEmail) return;
    // TODO: Implement email signup functionality
    console.log('Email signup:', email);
    setEmail('');
    alert('Thank you for signing up!');
  };

  // Format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Change log entries - you can update these with your actual updates
  const changeLogEntries = [
    {
      date: '2026-01-27',
      version: '1.3.0',
      title: 'Mobile Experience & Real-Time Features',
      changes: [
        'Redesigned mobile header - simplified to hamburger menu and logo icon for cleaner mobile experience',
        'Removed Sign Up button from mobile header - users are prompted to authenticate when needed',
        'Implemented real-time "who\'s online" feature - only active players viewing the game are shown',
        'Added scoring lock toggle - hosts can lock scores for completed sessions to prevent further changes',
        'Added game deletion feature - hosts can now delete games directly from the My Games view',
        'Improved game history filtering - players now only see games they participated in',
        'Streamlined navigation - removed Review and Actions cards for cleaner interface',
        'Updated branding to "The Creative Coaster" across the platform',
        'Connected custom domain creativecoaster.design with SSL certificate',
        'Updated footer contact link to open email client for tim@creativecoaster.design'
      ]
    },
    {
      date: '2025-11-24',
      version: '1.2.0',
      title: 'Voice System & UX Improvements',
      changes: [
        'Improved AI voice reading quality with higher quality model and optimized settings',
        'Fixed audio playback issues - audio now plays reliably without being cut off',
        'Removed timing fields from client changes setup (now using manual voice triggering)',
        'Enhanced real-time updates - host view automatically shows when players join',
        'Added design upload status display for hosts (replaced upload buttons)',
        'Fixed leaderboard to correctly display all submissions',
        'Added ability for players to replace uploaded designs',
        'Improved join code validation with multiple fallback methods',
        'Fixed Session Complete message display for hosts after timer ends',
        'Enhanced error handling for 406 errors when fetching game data'
      ]
    },
    {
      date: '2025-11-24',
      version: '1.1.0',
      title: 'Game Setup & Flow Improvements',
      changes: [
        'Reordered game setup fields - Number of Sessions now appears before Designers',
        'Made designer information optional in game setup',
        'Improved real-time participant tracking - players appear automatically in host view',
        'Fixed empty state messaging - changed "No designers added yet" to "No players have joined"',
        'Enhanced timer synchronization between host and players',
        'Improved game status management and transitions',
        'Fixed waiting screen visibility and stability'
      ]
    },
    {
      date: '2025-01-15',
      version: '1.0.0',
      title: 'Initial Release',
      changes: [
        'Launched Client Roller Coaster design challenge platform',
        'Added game creation and management features',
        'Implemented submission and scoring system',
        'Created leaderboard functionality',
        'Integrated AI voice reading for client changes',
        'Added real-time game synchronization',
        'Implemented timer system with database sync'
      ]
    },
    {
      date: '2025-01-10',
      version: '0.9.0',
      title: 'Beta Release',
      changes: [
        'Added MagicBento component with game information cards',
        'Implemented footer with news and updates section',
        'Added email signup functionality',
        'Created responsive design for mobile devices'
      ]
    },
    {
      date: '2025-01-05',
      version: '0.8.0',
      title: 'UI Improvements',
      changes: [
        'Redesigned homepage with Hyperspeed animation',
        'Added timing and scoring cards',
        'Improved resource management interface',
        'Enhanced user experience with better navigation'
      ]
    }
  ];

  return (
    <footer className="footer">
      {/* Main Footer Content */}
      <div className="footer-main">
        <div className="footer-main-row">
          <div className="footer-email-section">
            <p className="footer-email-text">Sign up to get product updates</p>
            <form onSubmit={handleEmailSubmit} className="footer-email-form">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="footer-email-input"
                required
              />
              <MorphButton
                type="submit"
                iconOnly
                variant="secondary"
                aria-label="Sign up"
                disabled={!canSubmitEmail}
              />
            </form>
          </div>

          <div className="footer-links-column">
            <h4 className="footer-links-title">Company</h4>
            <button type="button" className="footer-link footer-link-button" onClick={() => setIsChangeLogOpen(true)}>Change Log</button>
            <a href="mailto:tim@creativecoaster.design" className="footer-link">Contact Me</a>
          </div>

          <div className="footer-marquee" aria-hidden="true">
            <CurvedLoop
              text="The Creative Coaster"
              baseVelocity={8}
              fadePercent={18}
              curveAmount={-70}
              direction="right"
              outline
              color="rgba(255, 255, 255, 0.75)"
              strokeWidth={2.5}
              draggable={false}
              font={{
                fontFamily: "'Orbitron', sans-serif",
                fontWeight: 700,
                fontSize: 160,
                letterSpacing: '0.06em',
                lineHeight: '1.2em'
              }}
            />
          </div>
        </div>

        <div className="footer-made-with">
          <span>Made with</span>
          <img src="/cursor.svg" alt="Cursor" className="footer-icon footer-cursor-icon" />
          <img src="/supabase-logo-icon.svg" alt="Supabase" className="footer-icon footer-supabase-icon" />
          <img src="/mf-doom.svg" alt="MF Doom" className="footer-icon footer-mfdoom-icon" />
          <Heart className="footer-icon footer-heart-icon" size={16} />
          <Coffee className="footer-icon" size={16} />
        </div>
      </div>

      {/* Change Log Modal */}
      {isChangeLogOpen && (
        <>
          {/* Custom overlay to ensure it shows */}
          <div 
            className="changelog-overlay"
            onClick={() => setIsChangeLogOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 10000,
              cursor: 'pointer'
            }}
          />
          <Dialog open={isChangeLogOpen} onOpenChange={setIsChangeLogOpen}>
            <DialogContent 
              className="changelog-modal"
            >
              <DialogHeader>
                <DialogTitle className="changelog-title">Change Log</DialogTitle>
                <DialogDescription className="changelog-description">
                  All updates and improvements to the platform
                </DialogDescription>
              </DialogHeader>
              <div className="changelog-content">
                {changeLogEntries.map((entry, index) => (
                  <div key={index} className="changelog-entry">
                    <div className="changelog-entry-header">
                      <div className="changelog-entry-meta">
                        <span className="changelog-date">{entry.date}</span>
                        <span className="changelog-version">v{entry.version}</span>
                      </div>
                      <h3 className="changelog-entry-title">{entry.title}</h3>
                    </div>
                    <ul className="changelog-changes">
                      {entry.changes.map((change, changeIndex) => (
                        <li key={changeIndex} className="changelog-change-item">
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </footer>
  );
};

export default Footer;

